import React, { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { Formik, Field } from 'formik';
import * as Yup from 'yup';
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  Select,
  MenuItem,
  Chip,
  Grid,
  Alert,
} from '@weave-mui/material';
import LinearProgress, {
  linearProgressVariants,
} from '@weave-mui/linear-progress';
import { Close as CloseIcon } from '@mui/icons-material';
import { shareTestSuite } from 'store/actions/test_suites_actions';

/**
 * Validation schema for Share Test Suite form
 * Validates email input, selected role, and shared users list
 * @param {string} suiteOwnerEmail - The email of the test suite creator/owner (to prevent sharing with)
 */
const getValidationSchema = (suiteOwnerEmail) =>
  Yup.object().shape({
    emailInput: Yup.string()
      .trim()
      .test(
        'valid-emails',
        'Please enter valid email addresses',
        function (value) {
          if (!value || value.trim() === '') return true; // Empty is ok, just can't add

          const emails = value.split(',').map((e) => e.trim());
          const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

          return emails.every((email) => emailRegex.test(email));
        }
      )
      .test('not-owner', 'Cannot share with the suite owner', function (value) {
        if (!value || value.trim() === '' || !suiteOwnerEmail) return true;

        const emails = value
          .split(',')
          .map((e) => e.trim().toLowerCase())
          .filter((e) => e);

        return !emails.includes(suiteOwnerEmail.toLowerCase());
      }),

    selectedRole: Yup.string().required('Please select a role'),

    sharedUsers: Yup.array()
      .of(
        Yup.object().shape({
          email: Yup.string().email('Invalid email').required('Email required'),
          role: Yup.string().required('Role required'),
        })
      )
      .test(
        'unique-emails',
        'Duplicate email addresses found',
        function (users) {
          if (!users || users.length === 0) return true;
          const emails = users.map((u) => u.email.toLowerCase());
          return emails.length === new Set(emails).size;
        }
      )
      .test('not-owner', 'Cannot share with the suite owner', function (users) {
        if (!users || users.length === 0 || !suiteOwnerEmail) return true;
        const ownerEmail = suiteOwnerEmail.toLowerCase();
        return !users.some((u) => u.email.toLowerCase() === ownerEmail);
      }),
  });

/**
 * ShareTestSuiteModal - A modal component for sharing test suites with other users
 *
 * This component uses Formik and Yup for form state management and validation.
 * All form state is managed internally by Formik - no external state synchronization needed.
 *
 * @param {Object} props - Component props
 * @param {boolean} [props.open=true] - Whether the modal is open
 * @param {Function} props.onClose - Function to call when closing the modal
 * @param {Function} [props.onSubmit] - Function to handle form submission (from modal system)
 * @param {Function} [props.onDataUpdate] - Function to update modal state and clear ModalContainer errors
 * @param {Object} [props.data] - Modal data from the modal management system (used for initial values)
 * @param {boolean} [props.loading=false] - Loading state from modal system
 * @param {Object} [props.errors={}] - Errors from ModalContainer
 * @param {string} [props.title] - Modal title from config
 * @param {string} [props.submitText] - Submit button text from config
 * @param {string} [props.cancelText] - Cancel button text from config
 * @param {Object} props.userInfo - Current user information object
 * @param {Array} props.roles - Array of available user roles for permissions
 * @param {string} [props.testsuiteid] - ID of the test suite to share (legacy support)
 * @param {string} [props.testSuiteName] - Name of the test suite for display (legacy support)
 * @param {Object} [props.userlist] - Object containing existing shared users (legacy support)
 * @returns {JSX.Element} The rendered share modal component
 */
function ShareTestSuiteModal({
  open = true,
  onClose,
  onSubmit,
  onDataUpdate, // Re-added: Used to clear ModalContainer errors
  data = {},
  loading = false,
  errors = {}, // Re-added: errors from ModalContainer
  title = 'Share test suite',
  submitText = 'Save',
  cancelText = 'Cancel',
  userInfo,
  roles = [],
  // Legacy props for backward compatibility
  testsuiteid,
  testSuiteName = '',
  userlist = { users: [] },
  createdBy, // Legacy prop for suite owner email
}) {
  const dispatch = useDispatch();

  // Use data from modal system or fall back to legacy props
  const modalData = data.testSuite || {
    testsuiteid,
    testsuitename: testSuiteName,
    userlist,
  };

  // Legacy loading state support
  const isLoading = loading;

  const displayName = modalData.testsuitename || testSuiteName || 'Unknown';

  // Get the test suite owner/creator email - NOT the logged-in user
  // The owner is the person who created the suite and should not be in the shared list
  const suiteOwnerEmail =
    modalData.createdby || data.createdby || createdBy || '';

  /**
   * Clear both Formik errors and ModalContainer errors
   * @param {Function} setFormikErrors - Formik's setErrors function
   * @param {Function} setFieldError - Formik's setFieldError function (optional)
   * @param {string} specificField - Clear a specific field error (optional)
   */
  const clearAllErrors = (
    setFormikErrors,
    setFieldError = null,
    specificField = null
  ) => {
    // Clear all Formik errors
    setFormikErrors({});

    // Clear specific field error if provided
    if (setFieldError && specificField) {
      setFieldError(specificField, undefined);
    }

    // Clear ModalContainer errors (if onDataUpdate is available)
    if (onDataUpdate && Object.keys(errors).length > 0) {
      // Trigger onDataUpdate with empty update to clear errors
      // The ModalContainer's handleFormUpdate already clears errors
      onDataUpdate({});
    }
  };

  // Determine initial form values - memoize to compare with current values
  const initialValues = useMemo(() => {
    const userListToUse = modalData.userlist || userlist;
    const initialSharedUsers =
      data.sharedUsers ||
      (userListToUse?.users
        ? userListToUse.users.map((user) => ({
            email: user.user,
            role: user.role,
          }))
        : []);

    // Filter out ONLY the suite owner's email from initial shared users to prevent validation errors
    // The owner/creator should never be in the shared users list as they have implicit access
    // However, other users (who are shared with) should remain even if they're the ones viewing the modal
    const filteredSharedUsers = suiteOwnerEmail
      ? initialSharedUsers.filter(
          (user) => user.email.toLowerCase() !== suiteOwnerEmail.toLowerCase()
        )
      : initialSharedUsers;

    return {
      emailInput: data.emailInput || '',
      selectedRole:
        data.selectedRole || (roles.length > 0 ? roles[0].rolename : ''),
      sharedUsers: filteredSharedUsers,
    };
  }, [modalData.userlist, userlist, data, roles, suiteOwnerEmail]);

  /**
   * Check if form values have changed from initial state
   */
  const hasFormChanged = (currentValues) => {
    // Compare sharedUsers arrays
    if (currentValues.sharedUsers.length !== initialValues.sharedUsers.length) {
      return true;
    }

    // Deep compare each user
    const sortedCurrent = [...currentValues.sharedUsers].sort((a, b) =>
      a.email.localeCompare(b.email)
    );
    const sortedInitial = [...initialValues.sharedUsers].sort((a, b) =>
      a.email.localeCompare(b.email)
    );

    return !sortedCurrent.every(
      (user, index) =>
        user.email === sortedInitial[index].email &&
        user.role === sortedInitial[index].role
    );
  };

  /**
   * Handle form submission with Formik
   * Validates and submits the shared users list
   */
  const handleFormikSubmit = async (values, { setSubmitting, setErrors }) => {
    const users = values.sharedUsers.map((user) => ({
      email: user.email,
      role: user.role,
    }));

    // If using new modal system, use onSubmit prop
    if (onSubmit) {
      // ModalContainer handles error extraction and display via errors prop
      // Just call onSubmit and let it handle everything
      await onSubmit({
        sharedUsers: users,
        testSuiteId: modalData.testsuiteid,
      });
    } else {
      // Legacy implementation - use .then/.catch for proper error handling with Redux thunk
      dispatch(shareTestSuite(modalData.testsuiteid || testsuiteid, users))
        .then(() => {
          onClose();
        })
        .catch((error) => {
          // Extract error message from API response
          let errorMessage = 'An error occurred while sharing';

          if (error.detail) {
            errorMessage = error.detail;
          } else if (error.response?.data?.detail) {
            errorMessage = error.response.data.detail;
          } else if (error.response?.data?.message) {
            errorMessage = error.response.data.message;
          } else if (error.message) {
            errorMessage = error.message;
          }

          // Set Formik errors
          setErrors({
            general: errorMessage,
          });
          setSubmitting(false);
        });
    }
  };

  /**
   * Add emails to the shared users list
   */
  const handleAddEmails = (values, setFieldValue, setFieldError, setErrors) => {
    const emailInput = values.emailInput?.trim();
    if (!emailInput) return;

    // Clear all errors (both Formik and ModalContainer)
    clearAllErrors(setErrors, setFieldError, 'emailInput');

    // Split by comma and clean up emails
    const emails = emailInput
      .split(',')
      .map((email) => email.trim().toLowerCase())
      .filter((email) => email);

    // Validate emails
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidEmails = emails.filter((email) => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      setFieldError('emailInput', 'Please enter valid email addresses');
      return;
    }

    // Check for suite owner's email (not logged-in user)
    if (suiteOwnerEmail) {
      const ownerEmail = suiteOwnerEmail.toLowerCase();
      if (emails.includes(ownerEmail)) {
        setFieldError('emailInput', 'Cannot share with the suite owner');
        return;
      }
    }

    // Remove duplicates and existing emails
    const existingEmails = values.sharedUsers.map((user) =>
      user.email.toLowerCase()
    );
    const newEmails = emails.filter((email) => !existingEmails.includes(email));

    if (newEmails.length === 0) {
      setFieldError('emailInput', 'Email is already in the shared list');
      return;
    }

    // Add new emails with selected role
    const newUsers = newEmails.map((email) => ({
      email,
      role: values.selectedRole,
    }));

    const updatedUsers = [...values.sharedUsers, ...newUsers];
    setFieldValue('sharedUsers', updatedUsers);
    setFieldValue('emailInput', '');
  };

  /**
   * Remove a user from the shared users list
   */
  const handleRemoveUser = (
    emailToRemove,
    values,
    setFieldValue,
    setFieldError,
    setErrors
  ) => {
    // Clear all errors (both Formik and ModalContainer)
    clearAllErrors(setErrors, setFieldError, 'emailInput');

    const updatedUsers = values.sharedUsers.filter(
      (user) => user.email !== emailToRemove
    );
    setFieldValue('sharedUsers', updatedUsers);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getValidationSchema(suiteOwnerEmail)}
      onSubmit={handleFormikSubmit}
      enableReinitialize={false}
    >
      {({
        values,
        errors: formikErrors,
        touched,
        isSubmitting,
        setFieldValue,
        setFieldError,
        setErrors,
        handleSubmit: formikHandleSubmit,
      }) => {
        // Check if form has changed to enable/disable Save button
        const isFormChanged = hasFormChanged(values);
        // Create modal action buttons with Formik context
        const actionButtons = (
          <Box>
            <Box
              sx={{
                flexDirection: 'row',
                justifyContent: 'flex-end',
                display: 'flex',
                gap: 2,
              }}
            >
              <Button
                variant="outlined"
                onClick={onClose}
                disabled={isLoading || isSubmitting}
              >
                {cancelText}
              </Button>
              <Button
                variant="contained"
                onClick={formikHandleSubmit}
                disabled={isLoading || isSubmitting || !isFormChanged}
              >
                {isLoading || isSubmitting ? 'Saving...' : submitText}
              </Button>
            </Box>
          </Box>
        );

        return (
          <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="share-modal-title"
            aria-describedby="share-modal-description"
            header={`${title} ${displayName}`}
            actions={actionButtons}
            sx={{
              '& .MuiModalWeave-box': {
                width: '800px !important',
                maxWidth: '90vw',
              },
              '& .MuiModalWeave-actions': {
                padding: '0px 32px 16px 32px',
              },
              '& .MuiModalWeave-child.MuiBox-root': {
                padding: '16px 32px',
              },
            }}
          >
            <Box>
              {/* Loading indicator */}
              {(isLoading || isSubmitting) && (
                <LinearProgress
                  variant={linearProgressVariants.INDETERMINATE}
                  aria-label="Sharing progress indicator"
                  sx={{ mb: 2 }}
                />
              )}

              {/* Display errors from ModalContainer (server-side) or Formik (client-side) */}
              {(errors.general || formikErrors.general) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {errors.general || formikErrors.general}
                </Alert>
              )}
              {formikErrors.sharedUsers &&
                typeof formikErrors.sharedUsers === 'string' && (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {formikErrors.sharedUsers}
                  </Alert>
                )}

              <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                DL's are not supported, add individual emails to share test
                suite.
              </Typography>

              <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
                <Grid item xs={6}>
                  <Typography
                    variant="caption"
                    sx={{ mb: 1, fontWeight: 'medium' }}
                  >
                    Emails
                  </Typography>
                  <Field name="emailInput">
                    {({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        variant="outlined"
                        placeholder="Enter email addresses"
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddEmails(
                              values,
                              setFieldValue,
                              setFieldError,
                              setErrors
                            );
                          }
                        }}
                        onChange={(e) => {
                          setFieldValue('emailInput', e.target.value);
                          // Always clear all errors when user types (regardless of which error exists)
                          clearAllErrors(
                            setErrors,
                            setFieldError,
                            'emailInput'
                          );
                        }}
                        size="small"
                        error={touched.emailInput && !!formikErrors.emailInput}
                        helperText={
                          touched.emailInput && formikErrors.emailInput ? (
                            formikErrors.emailInput
                          ) : (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              Enter email addresses, separated by commas (e.g.,
                              example1@mail.com, example2@mail.com).
                            </Typography>
                          )
                        }
                        sx={{
                          '& .MuiInputBase-root': {
                            height: '40px',
                            minHeight: '40px',
                          },
                        }}
                      />
                    )}
                  </Field>
                </Grid>

                <Grid
                  item
                  xs={4}
                  sx={{ alignSelf: 'stretch', paddingTop: '21px' }}
                >
                  <Typography
                    variant="caption"
                    sx={{ mb: 1, fontWeight: 'medium' }}
                  >
                    Permissions
                  </Typography>
                  <Field name="selectedRole">
                    {({ field }) => (
                      <FormControl
                        fullWidth
                        size="small"
                        error={
                          touched.selectedRole && !!formikErrors.selectedRole
                        }
                      >
                        <Select
                          {...field}
                          displayEmpty
                          variant="outlined"
                          onChange={(e) => {
                            setFieldValue('selectedRole', e.target.value);
                          }}
                          sx={{
                            '& .MuiSelect-select': {
                              height: '40px !important',
                            },
                          }}
                        >
                          {roles.map((role) => (
                            <MenuItem key={role.rolename} value={role.rolename}>
                              {role.rolename}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                  </Field>
                </Grid>

                <Grid
                  item
                  xs={2}
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    paddingTop: '0px !important',
                    marginTop: '-16px',
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() =>
                      handleAddEmails(
                        values,
                        setFieldValue,
                        setFieldError,
                        setErrors
                      )
                    }
                    disabled={
                      !values.emailInput?.trim() || !values.selectedRole
                    }
                    size="small"
                    sx={{ height: '40px', minHeight: '40px' }}
                  >
                    Add
                  </Button>
                </Grid>
              </Grid>

              {/* Shared Users List */}
              {values.sharedUsers.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {values.sharedUsers.map((user, index) => (
                      <Chip
                        key={`${user.email}-${index}`}
                        label={`${user.email} (${user.role})`}
                        onDelete={() =>
                          handleRemoveUser(
                            user.email,
                            values,
                            setFieldValue,
                            setFieldError,
                            setErrors
                          )
                        }
                        deleteIcon={
                          <CloseIcon sx={{ width: 24, height: 24 }} />
                        }
                        variant="filled"
                        size="small"
                        sx={{
                          minWidth: 70,
                          height: 24,
                          fontSize: '12px',
                          fontWeight: 600,
                          lineHeight: '14px',
                          backgroundColor: 'rgba(128, 128, 128, 0.15)',
                          '&:hover': {
                            backgroundColor: 'rgba(128, 128, 128, 0.25)',
                          },
                          borderRadius: '4px',
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}
            </Box>
          </Modal>
        );
      }}
    </Formik>
  );
}

export default ShareTestSuiteModal;
